import {useUser} from './use-auth.tsx';
import {FC, useCallback} from 'react';
import {UsersService} from '../api/generated/UsersService.ts';
import {createForm} from '../forms';
import {UpdateUserPasswordRequest} from '../api/generated/index.defs.ts';
import {useNotification} from '../hooks/use-notifications.tsx';
import {Button, Group, Stack} from '@mantine/core';
import {nonEmptyString} from '../utils/zod.ts';
import {z} from 'zod';
import {SubmitHandler} from 'react-hook-form';

const Form = createForm<ChangePasswordFormSchema>();

type ChangePasswordProps = {
  onComplete: () => void;
};

export const ChangePassword: FC<ChangePasswordProps> = ({onComplete}) => {
  const notification = useNotification();
  const user = useUser();
  const id = user.id;

  const onSubmit = useCallback<SubmitHandler<ChangePasswordFormSchema>>(
    async (values) => {
      const response = await UsersService.updatePassword({
        id,
        body: {
          currentPassword: values.currentPassword || '',
          newPassword: values.newPassword || '',
          confirmNewPassword: values.confirmNewPassword || '',
        },
      });

      if (response.hasErrors) {
        return response;
      }

      notification.success('Password Changed');
      onComplete();
    },
    [id, notification, onComplete]
  );

  return (
    <Form schema={changePasswordFormSchema} onSubmit={onSubmit}>
      <Stack>
        <Form.PasswordInput name="currentPassword" label="Current Password" />
        <Form.PasswordInput
          name="newPassword"
          label="New Password"
          description="Min Length: 8 Characters. At least one uppercase, lowercase and symbol required."
        />
        <Form.PasswordInput
          name="confirmNewPassword"
          label="Confirm New Password"
        />
        <Form.Actions>
          <Group align={'center'} grow>
            <Button type="submit">Change Password</Button>
          </Group>
        </Form.Actions>
      </Stack>
    </Form>
  );
};

const changePasswordFormSchema = z.object({
  currentPassword: nonEmptyString(),
  newPassword: nonEmptyString()
    .min(8, 'At least 8 characters required')
    .regex(/[a-z]/, 'At least one lowercase letter required')
    .regex(/[A-Z]/, 'At least one uppercase letter required')
    .regex(/[\d]/, 'At least one digit required')
    .regex(/[^\s\w\d]/, 'At least one non alphanumeric character required'),
  confirmNewPassword: nonEmptyString(),
}) satisfies z.ZodSchema<UpdateUserPasswordRequest>;
type ChangePasswordFormSchema = z.TypeOf<typeof changePasswordFormSchema>;